import axios from 'axios';
import { BrandPayload, BrandResponse } from "../types/BrandModels";
import { debug } from "../utils/debugLogger";

// Always use environment variable - no fallbacks
const BASE_URL = process.env.REACT_APP_CUSTOMER_API_URL;

if (!BASE_URL) {
  debug.error('REACT_APP_CUSTOMER_API_URL is not set in environment variables');
}

debug.log(`Using Brand API URL (${process.env.REACT_APP_ENV}):`, BASE_URL);



/** customer-management's answer to a Brand-details save (SGL-513). */
export type BrandStatusUpdate = "activated" | "unchanged" | "failed" | undefined;

export const submitBrandDetailsFromOnboarding = async (
  customerId: string | null,
  answers: Record<string, any>,
  idToken: string,
  brandId?: string | null
): Promise<string | undefined> =>
  (await saveBrandDetails(customerId, answers, idToken, brandId)).brandId;

/** Same save, also returning whether it moved the Brand ONBOARDING → ACTIVE. */
export const saveBrandDetails = async (
  customerId: string | null,
  answers: Record<string, any>,
  idToken: string,
  brandId?: string | null
): Promise<{ brandId: string | undefined; statusUpdate: BrandStatusUpdate }> => {
  if (!customerId) {
    throw new Error("Customer ID is required to submit brand details");
  }

  // Transform to new structured payload format
  const brandPayload: BrandPayload = {
    brand: {
      name: answers["brand_name"]?.answer_value || "",
      business_type: answers["business_type"]?.answer_value || "SERVICE",
      industry: answers["industry"]?.answer_value || "",
      website_url: answers["website"]?.answer_value || "",
      phone_number: answers["phone_number"]?.answer_value || "",
      service_areas: answers["service_area"]?.answer_value || [],
      services: answers["services_offered"]?.answer_value || [],
      products: answers["products_offered"]?.answer_value || [],
      tagline: answers["brand_tagline"]?.answer_value || "",
    },
    visual: {
      logo_url: answers["logo_url"] || "",
      logo_media_id: answers["logo_media_id"] || "",
      logo_url_dark: answers["logo_url_dark"] || "",
      logo_media_id_dark: answers["logo_media_id_dark"] || "",
      colors: answers["brand_colors"]?.map((color: string, index: number) => ({
        hex: color,
        role: index === 0 ? "primary" : (index === 1 ? "secondary" : "accent")
      })) || [{ hex: "#2f4941", role: "primary" }],
    },
    content_guidelines: {
      brand_voice: answers["brand_voice"]?.answer_value || "",
      target_audience: answers["target_audience"]?.answer_value || "",
      primary_cta: answers["primary_cta"]?.answer_value || [],
      avoid_phrases: answers["avoid_words"]?.answer_value || [],
      must_include_phrases: answers["must_include_words"]?.answer_value || [],
    }
  };

  try {
    const url = `${BASE_URL}/${customerId}/brand-details`;
    const method = brandId ? 'PUT' : 'POST';
    const isUpdate = !!brandId;
    
    debug.log('📤 Sending brand data to backend:', {
      method,
      endpoint: url,
      isUpdate,
      brandId: brandId || 'new',
      payload: brandPayload
    });
    
    // Use PUT for update, POST for create - same URL for both
    const response = brandId 
      ? await axios.put(url, brandPayload, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          }
        })
      : await axios.post(url, brandPayload, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          }
        });
    
    debug.log(isUpdate ? '✅ Brand update successful:' : '✅ Brand creation successful:', response.data);
    return { brandId: response.data.brand_id, statusUpdate: response.data.brand_status_update };
  } catch (error: any) {
    debug.error("❌ Failed to submit brand details:", {
      error: error.response?.data || error.message,
      status: error.response?.status,
      method: brandId ? 'PUT' : 'POST',
      url: `${BASE_URL}/${customerId}/brand-details`,
      customerId,
      brandId,
    });
    throw error;
  }
};

/**
 * Load brand(s) for a customer. Returns `{ brands: [] }` when none exist yet
 * (including HTTP 404) so new users see an empty form without a false error.
 */
export const fetchBrands = async (
  customerId: string,
  idToken: string
): Promise<{ brands: BrandResponse[] }> => {
  if (!BASE_URL) {
    throw new Error("REACT_APP_CUSTOMER_API_URL is not configured");
  }

  try {
    const response = await axios.get(
      `${BASE_URL}/${customerId}/brand-details`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    const brandData = response.data as BrandResponse;

    if (Array.isArray(response.data)) {
      return { brands: response.data };
    }
    if (brandData?.brand_id) {
      return { brands: [brandData] };
    }

    return { brands: [] };
  } catch (error: any) {
    const status = error?.response?.status;
    if (status === 404) {
      return { brands: [] };
    }
    throw error;
  }
};


